import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend,
} from "recharts";
import { CalendarClock, PhoneCall, Target, Flame } from "lucide-react";
import ReportFilterBar from "@/components/ops/ReportFilterBar";
import LoadingOverlay from "@/components/common/LoadingOverlay";

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Kpi({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

// Interpolate a blue → green colour based on value/max intensity.
function heatColor(value, max, metric) {
  if (!value || !max) return "rgba(148, 163, 184, 0.08)";
  const t = Math.min(1, value / max);
  if (metric === "conv_rate") {
    // green scale — deeper green = higher conv rate
    const alpha = 0.15 + t * 0.75;
    return `rgba(34, 197, 94, ${alpha})`;
  }
  if (metric === "contact_rate") {
    // amber scale
    const alpha = 0.12 + t * 0.75;
    return `rgba(245, 158, 11, ${alpha})`;
  }
  // calls → blue
  const alpha = 0.1 + t * 0.8;
  return `rgba(10, 132, 255, ${alpha})`;
}

function Legend2({ metric }) {
  const items = [0.0, 0.25, 0.5, 0.75, 1.0];
  return (
    <div className="flex items-center gap-2 text-[10px] text-slate-500">
      <span className="uppercase">Low</span>
      <div className="flex">
        {items.map((t, i) => (
          <div key={i} className="w-6 h-3 border border-slate-100"
               style={{ background: heatColor(t, 1, metric) }} />
        ))}
      </div>
      <span className="uppercase">High</span>
    </div>
  );
}

export default function PeakHoursPage() {
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState("calls"); // calls | contact_rate | conv_rate

  useEffect(() => {
    setLoading(true);
    api.get("/ops/reports/peak-hours", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [params]);

  // Convert cells array into a 7×24 lookup: grid[dow][hour]
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ calls: 0, contactable: 0, converted: 0, contact_rate: 0, conv_rate: 0 })));
    (data?.cells || []).forEach((c) => { g[c.dow][c.hour] = c; });
    return g;
  }, [data]);

  const maxVal = useMemo(() => {
    if (!data?.cells) return 0;
    return Math.max(...data.cells.map((c) => c[metric] || 0), 0);
  }, [data, metric]);

  const totals = data?.totals || {};

  const hourChart = useMemo(
    () => (data?.by_hour || []).map((h) => ({
      hour: `${String(h.hour).padStart(2, "0")}h`,
      calls: h.calls,
      conv_rate: h.conv_rate,
    })),
    [data]
  );

  const dowChart = useMemo(
    () => (data?.by_dow || []).map((d) => ({
      day: d.dow_label,
      calls: d.calls,
      conv_rate: d.conv_rate,
    })),
    [data]
  );

  const metricLabels = {
    calls: "Call volume",
    contact_rate: "Contact rate %",
    conv_rate: "Conversion rate %",
  };

  return (
    <div className="space-y-6" data-testid="peakhours-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Peak call hours
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            When are calls actually connecting and converting? A 7-day × 24-hour heatmap of the ops floor.
          </p>
        </div>
        <ReportFilterBar onChange={setParams} testIdPrefix="peakhours" />
      </div>

      <LoadingOverlay loading={loading} testId="peakhours-loading">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Total calls" value={fmt(totals.total_calls)}
               sub="Across the selected window" icon={PhoneCall} testId="peakhours-kpi-calls" />
          <Kpi label="Overall contact %" value={fmtPct(totals.overall_contact_rate)}
               sub={`${fmt(totals.total_contactable)} contactable`} icon={CalendarClock} testId="peakhours-kpi-contact" />
          <Kpi label="Overall conv %" value={fmtPct(totals.overall_conv_rate)}
               sub={`${fmt(totals.total_converted)} conversions`} icon={Target} testId="peakhours-kpi-conv" />
          <Kpi label="Best slot"
               value={data?.best_slots?.[0] ? `${data.best_slots[0].dow_label} ${data.best_slots[0].hour}h` : "—"}
               sub={data?.best_slots?.[0] ? `${fmtPct(data.best_slots[0].conv_rate)} conv on ${fmt(data.best_slots[0].calls)} calls` : "Needs ≥20 calls in a cell"}
               icon={Flame} testId="peakhours-kpi-best" />
        </div>

        {/* Metric switcher */}
        <div className="glass-card p-3 mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase text-slate-500 font-medium">Metric</span>
            {Object.entries(metricLabels).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setMetric(k)}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${metric === k ? "bg-slate-900 text-white" : "bg-white/70 text-slate-600 hover:bg-slate-100"}`}
                data-testid={`peakhours-metric-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
          <Legend2 metric={metric} />
        </div>

        {/* HEATMAP */}
        <div className="glass-card p-5 mt-6 overflow-x-auto" data-testid="peakhours-heatmap">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">
            {metricLabels[metric]} — 7 × 24 grid (times shown in IST)
          </div>
          <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
            <thead>
              <tr>
                <th className="w-10"></th>
                {Array.from({ length: 24 }).map((_, h) => (
                  <th key={h} className="w-9 text-center text-slate-500 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((day, d) => (
                <tr key={day}>
                  <th className="text-right pr-2 font-medium text-slate-600 sticky left-0 bg-white/70">{day}</th>
                  {Array.from({ length: 24 }).map((_, h) => {
                    const cell = grid[d][h];
                    const value = cell[metric] || 0;
                    return (
                      <td
                        key={h}
                        className="w-9 h-9 text-center rounded relative group"
                        style={{ background: heatColor(value, maxVal, metric) }}
                        data-testid={`peakhours-cell-${d}-${h}`}
                      >
                        <div className="text-[10px] text-slate-800 tabular-nums leading-9">
                          {value > 0 ? (metric === "calls" ? fmt(value) : `${Number(value).toFixed(0)}`) : ""}
                        </div>
                        <div className="absolute z-10 hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap bg-slate-900 text-white text-[10px] rounded px-2 py-1 shadow-lg">
                          {day} · {h}h · {fmt(cell.calls)} calls · {fmtPct(cell.contact_rate)} contact · {fmtPct(cell.conv_rate)} conv
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {maxVal === 0 && (
            <div className="text-sm text-slate-500 py-6 text-center">No call activity in the selected window.</div>
          )}
        </div>

        {/* Hour-of-day + Day-of-week strips */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <div className="glass-card p-5" data-testid="peakhours-by-hour">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              By hour of day
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourChart} margin={{ top: 16, right: 20, left: 0, bottom: 8 }}>
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, k) => (k === "conv_rate" ? [`${Number(v).toFixed(1)}%`, "Conv rate"] : [fmt(v), "Calls"])} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="l" dataKey="calls" fill="#0A84FF" radius={[4, 4, 0, 0]} name="Calls" />
                  <Bar yAxisId="r" dataKey="conv_rate" fill="#22C55E" radius={[4, 4, 0, 0]} name="Conv rate %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass-card p-5" data-testid="peakhours-by-dow">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              By day of week
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dowChart} margin={{ top: 16, right: 20, left: 0, bottom: 8 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, k) => (k === "conv_rate" ? [`${Number(v).toFixed(1)}%`, "Conv rate"] : [fmt(v), "Calls"])} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="l" dataKey="calls" fill="#0A84FF" radius={[4, 4, 0, 0]} name="Calls">
                    {dowChart.map((e, i) => (
                      <Cell key={i} fill={i >= 5 ? "#94A3B8" : "#0A84FF"} />
                    ))}
                  </Bar>
                  <Bar yAxisId="r" dataKey="conv_rate" fill="#22C55E" radius={[4, 4, 0, 0]} name="Conv rate %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              Blue = weekday · Grey = weekend
            </div>
          </div>
        </div>

        {/* Best slots list */}
        {(data?.best_slots || []).length > 0 && (
          <div className="glass-card p-5 mt-8" data-testid="peakhours-best-slots">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              Top 5 slots by conversion rate (≥20 calls per cell)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {data.best_slots.map((s, i) => (
                <div key={`${s.dow}-${s.hour}`} className="rounded-xl p-3 border border-emerald-100 bg-emerald-50/60">
                  <div className="text-[10px] uppercase text-emerald-700 font-medium">#{i + 1}</div>
                  <div className="text-lg font-outfit font-semibold text-slate-900 mt-0.5">
                    {s.dow_label} · {String(s.hour).padStart(2, "0")}:00
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {fmtPct(s.conv_rate)} conv · {fmt(s.calls)} calls · {fmtPct(s.contact_rate)} contact
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </LoadingOverlay>
    </div>
  );
}
